import * as EstimatesActions from './estimate.actions';

import {createEntityAdapter, EntityAdapter} from "@ngrx/entity";
import {createReducer, on} from "@ngrx/store";
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {EstimateDocument} from '@accounting/_common/models/estimate.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const estimateFeatureKey = 'estimates';

export function sortByDate(a: EstimateDocument, b: EstimateDocument) {
    if (a.date !== b.date) {
        return StoredComponentHelperService.sortArrayComparer(a, b, 'date');
    }

    return (a.id || '').localeCompare(b.id || '');
}

export const adapter: EntityAdapter<EstimateDocument> = createEntityAdapter<EstimateDocument>({
    sortComparer: sortByDate
});

export type EstimatesState = EntityStateModel<EstimateDocument, any>;

export const initialState: EstimatesState = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: []
});

export const estimatesReducer = createReducer(
    initialState,

    on(EstimatesActions.CheckState, (state, action) => {
        if (action.payload.modalView) {
            return adapter.removeAll({
                ...state,
                savedFilter: !action.payload.close && state.filter ? state.filter : undefined,
                pagination: initialState.pagination,
                filter: action.payload.close ? state.savedFilter : undefined
            });
        } else {
            if (action.payload.close) {
                return adapter.setAll(action.payload.items, {
                    ...state,
                    pagination: {...state.pagination, number: 0},
                    componentOpened: false
                });
            }

            return {...state, componentOpened: true};
        }
    }),

    on(EstimatesActions.LoadList, (state, action) => ({
        ...state,
        listLoading: StoredComponentHelperService.updateListLoading(action.payload, state.ids)
    })),

    on(EstimatesActions.LoadListSuccess, (state, action) => {
        return action.payload.clearState
            ? adapter.setAll(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined
            })
            : adapter.upsertMany(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined
            });
    }),

    on(EstimatesActions.LoadListFailure, (state) => ({
        ...state,
        listLoading: undefined
    })),

    on(EstimatesActions.SetPagination, (state, action) => ({
        ...state,
        pagination: {...state.pagination, number: action.payload ? StoredComponentHelperService.setNextPage(state.pagination, state.ids?.length) : 0}
    })),

    on(EstimatesActions.SetLoading, (state, action) => ({
        ...state,
        listLoading: action.payload
    })),

    on(EstimatesActions.SetItemsLoading, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id, action.payload.isStart)
    })),

    on(EstimatesActions.UpdateItemSuccess, (state, action) => {
        if (action.payload.parent?.id) {
            const parentItem = state.entities[action.payload.parent.id];

            const updatedChildren = parentItem?.children
                ? parentItem.children.map(child =>
                    child.id === action.payload.id ? {...child, ...action.payload} : child
                )
                : [];

            if (!updatedChildren.some(child => child.id === action.payload.id)) {
                updatedChildren.push(action.payload);
            }

            return adapter.updateOne({
                id: action.payload.parent.id,
                changes: {children: updatedChildren}
            }, state);
        }

        return adapter.setOne(action.payload!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids)}
        });
    }),

    on(EstimatesActions.DeleteItem, (state, action) => {
        if (action.payload.parent?.id) {
            const parentItem = state.entities[action.payload.parent.id];

            const updatedChildren = parentItem?.children
                ? parentItem.children.filter(child => child.id !== action.payload.id)
                : [];

            return adapter.updateOne({
                id: action.payload.parent.id,
                changes: {children: updatedChildren}
            }, {
                ...state,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, true)
            });
        }

        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(EstimatesActions.DeleteItemSuccess, (state, action) => {
        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(EstimatesActions.DeleteItemFailure, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false)
    })),

    on(EstimatesActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const {selectAll: selectAllEntities} = adapter.getSelectors();
